import { FieldState, getIn } from 'final-form'
import { useForm } from 'react-final-form'

import { PecFormApi } from '../model'
import { toPecFormApi } from '../util'

/**
 * Instancias de FormApi ja interceptadas. E um WeakSet, e nao uma flag no proprio form, porque o
 * react-final-form monta o `form` do render prop como copia rasa (`{...form, reset}`): uma
 * propriedade seria copiada junto e faria o guarda pular o patch numa instancia nao interceptada.
 */
const interceptados = new WeakSet<PecFormApi>()

/**
 * Aplica o preserveFieldValueOnMount na instancia de FormApi do contexto.
 *
 * Precisa ser componente, e nao uma chamada no corpo do Form: o `form` que chega no render prop do
 * react-final-form e uma copia rasa (`{...form, reset}`), nao a instancia que o useField enxerga via
 * contexto. So o useForm() devolve a instancia certa.
 *
 * O patch acontece na fase de render, de proposito. Fazer por effect nao funcionaria: effect de pai
 * roda depois do effect dos filhos, e e justamente no mount effect dos campos que a lib faz o change
 * indevido. Mutar em render e impuro, mas e idempotente e a instancia e externa ao React. O WeakSet
 * garante que o duplo render do StrictMode nao empilhe wrappers.
 *
 * Renderizar como primeiro filho nao e obrigatorio para a correcao em si (os mount effects de toda a
 * arvore rodam depois do commit, entao o patch ja esta no lugar). Importa so para o primeiro frame:
 * o useState initializer do useField tambem chama getFieldState, e um campo que renderize antes
 * deste componente monta esse primeiro frame com o estado nao corrigido.
 */
export function PreserveFieldValueOnMount() {
  preserveFieldValueOnMount(toPecFormApi(useForm()))
  return null
}

/**
 * TODO #34593: Remover apos correcao do bug no react-final-form.
 *
 * Contorna a regressao do react-final-form v7.0.1 (https://github.com/final-form/react-final-form/issues/1095,
 * introduzida pelo https://github.com/final-form/react-final-form/pull/1069): no mount effect do
 * useField, quando getFieldState(name) devolve undefined a lib assume que o campo foi destruido e
 * faz form.change(name, initialValues[name]), descartando qualquer valor escrito depois da
 * inicializacao. Isso acontece sempre que o ultimo Field de um nome desmonta e outro monta depois
 * (abas, blocos condicionais, Tooltip do bold-ui que troca de forma), e tambem quando um campo
 * presente no initialValues monta pela primeira vez ja tendo recebido valor via form.change.
 *
 * A interceptacao e estreita: so devolve estado sintetico quando o form ja tem, no caminho do campo,
 * um valor diferente do initialValues, ou seja, exatamente quando a lib descartaria esse valor. Em
 * qualquer outro caso devolve undefined e o fallback nativo do useField roda inteiro.
 *
 * destroyOnUnregister e o discriminador exato, nao um meio-termo: com a flag desligada o final-form
 * nunca apaga o valor no unregister, entao um valor presente e diferente do inicial so pode ter sido
 * escrito de proposito. Com a flag ligada o apagamento e legitimo e restaurar o initialValues e o
 * comportamento correto, por isso a sintese e suprimida. A leitura fica dentro do getFieldState
 * porque destroyOnUnregister e mutavel na FormApi (tem setter e case no setConfig).
 *
 * O que se perde quando a sintese acontece: o useState initializer do useField deixa de rodar o
 * proprio fallback, que trata initialValue/defaultValue de campo e coage select multiple sem valor
 * para []. Na pratica so morde se o valor corrente for undefined num select multiple, ja que nos
 * demais casos existe valor para exibir.
 *
 * Muda um contrato publico: a doc do final-form define getFieldState como undefined para campo nao
 * registrado, e isso deixa de valer para campos com valor divergente do inicial. Nenhum consumidor
 * do projeto usa undefined como sentinela de "nao registrado" (todos leem via `?.`), mas quem
 * escrever codigo novo precisa saber.
 *
 * O getFieldState so e consumido pelo useField do react-final-form; o final-form nao o usa
 * internamente.
 */
export function preserveFieldValueOnMount(form: PecFormApi): void {
  if (interceptados.has(form)) return

  interceptados.add(form)

  const getFieldState = form.getFieldState.bind(form)

  form.getFieldState = (name: string) => {
    const fieldState = getFieldState(name)
    if (fieldState || form.destroyOnUnregister) return fieldState

    const { values, initialValues } = form.getState()
    const value = values && getIn(values, name)
    const initial = initialValues && getIn(initialValues, name)

    return value === initial ? undefined : synthesize(form, name, value, initial)
  }
}

function synthesize(form: PecFormApi, name: string, value: any, initial: any): FieldState<any> {
  return {
    active: false,
    blur: () => form.blur(name),
    change: (novoValor: any) => form.change(name, novoValor),
    data: {},
    dirty: true,
    dirtySinceLastSubmit: false,
    error: undefined,
    focus: () => form.focus(name),
    initial,
    invalid: false,
    length: undefined,
    modified: false,
    modifiedSinceLastSubmit: false,
    name,
    pristine: false,
    submitError: undefined,
    submitFailed: false,
    submitSucceeded: false,
    submitting: false,
    touched: false,
    valid: true,
    validating: false,
    value,
    visited: false,
  }
}
